<template>
  <div ref="tableRef" :class="['table-wrapper', overflow]">
    <TransitionFadeInOut>
      <FriedEmptyState
        v-if="isSuccess && !templates.length"
        class="empty-state"
        :title="t('nothing_found_here')"
        :description="t('this_folder_is_empty')"
        icon="eyes"
      />
      <FriedTable v-else class="template-table">
        <template #header>
          <FriedTableHeader>
            <FriedTableHeaderCell
              sortable
              :active="args.sortBy === ResourceSortAttribute.Title"
              :direction="args.sortOrder"
              @click="toggleSort(ResourceSortAttribute.Title)"
            >
              {{ t('Name') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              v-if="!isXXSmallScreenAndDown"
              sortable
              :active="args.sortBy === ResourceSortAttribute.UpdatedAt"
              :direction="args.sortOrder"
              @click="toggleSort(ResourceSortAttribute.UpdatedAt)"
            >
              {{ t('last-used') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              v-if="isSmallScreenAndUp"
              sortable
              :active="args.sortBy === ResourceSortAttribute.CrUseCount"
              :direction="args.sortOrder"
              @click="toggleSort(ResourceSortAttribute.CrUseCount)"
            >
              {{ t('dsr-content-lib__used') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              v-if="isXSmallScreenAndUp"
              sortable
              :active="args.sortBy === ResourceSortAttribute.LastUsed"
              :direction="args.sortOrder"
              @click="toggleSort(ResourceSortAttribute.LastUsed)"
            >
              {{ t('last-edited') }}
            </FriedTableHeaderCell>
          </FriedTableHeader>
        </template>
        <template #bodies>
          <TransitionAppearList>
            <template v-if="!initialLoading && !!templates.length">
              <FriedTableRow
                v-for="template in templates"
                :key="template.id"
                clickable
                @click="emit('select', template)"
              >
                <FriedTableCell class="name-cell">
                  <FriedParagraph
                    v-tooltip="{
                      content: template.title,
                      delay: { show: 300, hide: 100 },
                      placement: 'bottom',
                    }"
                    class="truncate one-line"
                  >
                    {{ template.title }}
                  </FriedParagraph>
                </FriedTableCell>
                <FriedTableCell v-if="!isXXSmallScreenAndDown" class="date-cell">
                  {{ formatDate(template.statistics.lastUsed) }}
                </FriedTableCell>
                <FriedTableCell v-if="isSmallScreenAndUp" min-width>
                  {{ template.statistics.usedCount }}
                </FriedTableCell>
                <FriedTableCell v-if="isXSmallScreenAndUp" class="date-cell">
                  {{ formatDate(template.updatedAt) }}
                </FriedTableCell>
              </FriedTableRow>
            </template>
          </TransitionAppearList>
          <template v-if="initialLoading || hasMore || fetchingMore">
            <FriedTableRow v-for="row in 5" :key="row">
              <FriedTableCell><FriedSkeleton type="h4" /></FriedTableCell>
              <FriedTableCell v-if="!isXXSmallScreenAndDown"
                ><FriedSkeleton type="h4"
              /></FriedTableCell>
              <FriedTableCell v-if="isSmallScreenAndUp">
                <FriedSkeleton type="h4" />
              </FriedTableCell>
              <FriedTableCell v-if="isXSmallScreenAndUp">
                <FriedSkeleton type="h4" />
              </FriedTableCell>
            </FriedTableRow>
          </template>
        </template>
      </FriedTable>
    </TransitionFadeInOut>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue';
import { useScroll } from '@vueuse/core';
import { ResourceSortAttribute } from '@getaccept/lib-shared/src/resources/types/enum/resource-sort-attribute';
import { SortOrder } from '@getaccept/fried-tofu';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import { DateTime } from 'luxon';
import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import { useI18n } from 'vue-i18n';
import { useAsyncData } from '@getaccept/lib-shared/src/composables/use-async-data';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import type { ResourcesDto } from '../../api/resources/dtos/resources-dto';
import { ResourceService } from '../../api/resources/services/resource.service';
import { defaultResourceFilter } from '../document-create.constants';

const {
  enabled = false,
  search = '',
  directory = '',
} = defineProps<{
  enabled?: boolean;
  search?: string;
  directory?: string;
  overflow?: 'visible' | 'hidden' | 'scroll' | 'auto' | 'initial' | 'inherit' | 'revert'; // TODO fried tofu v7 export overflow type
}>();
const emit = defineEmits<{ select: [Resource] }>();

const { t } = useI18n();
const { isSmallScreenAndUp, isXSmallScreenAndUp, isXXSmallScreenAndDown } = useBreakpoints();

const { formattedDate, parseLegacyDate } = DateHelper;

const formatDate = (date: string) =>
  date ? formattedDate(DateTime.fromMillis(parseLegacyDate(date))) : '';

const args = ref<ResourcesDto>({
  ...defaultResourceFilter,
  limit: 30,
  sortOrder: SortOrder.Asc,
  sortBy: ResourceSortAttribute.Title,
});

const isSuccess = ref(false);

const {
  result: templateData,
  loading: initialLoading,
  fetchMore,
  fetchingMore,
  execute: fetchTemplates,
  onResult: onSuccess,
} = useAsyncData(ResourceService.loadList, args, {
  initData: { resources: [] as Resource[], totalCount: 0 },
  keepPreviousResult: true,
  enabled: computed(() => enabled),
});

onSuccess(() => {
  isSuccess.value = true;
});

watch(
  () => search,
  () => {
    args.value = { ...args.value, searchTerms: search.split(' ') };
  },
  { immediate: true }
);

watch(
  () => directory,
  () => {
    args.value = { ...args.value, directory };
    fetchTemplates(args.value);
  },
  { immediate: true }
);

const templates = computed(() => templateData.value.resources);

const hasMore = computed(
  () => templates.value.length % args.value.limit === 0 && !initialLoading.value
);

const tableRef = ref<HTMLElement>();
const { arrivedState } = useScroll(tableRef);
const { bottom: hasScrolledToBottom } = toRefs(arrivedState);

watch(hasScrolledToBottom, () => {
  if (hasScrolledToBottom.value && hasMore.value) {
    fetchMore({
      args: { offset: templates.value.length, searchTerms: args.value.searchTerms },
      update: (prev, { resources: nextPage }) => ({
        resources: [...prev.resources, ...nextPage],
        totalCount: prev.totalCount,
      }),
    });
  }
});

const toggleSort = (sortBy: ResourceSortAttribute) => {
  args.value = {
    ...args.value,
    sortBy,
    sortOrder: args.value.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc,
  };
};
</script>

<style lang="scss" scoped>
@import '@getaccept/lib-shared/src/assets/scss/truncate-text';

.table-wrapper {
  overflow: v-bind(overflow);
  height: 100%;

  .empty-state {
    margin: var(--spacing-200) auto;
  }

  .template-table {
    width: inherit;

    .date-cell {
      white-space: nowrap;
    }
  }

  &:not(.auto) {
    width: 100%;
    border: 2px solid transparent;
  }

  &.auto {
    .template-table {
      margin: 2px;

      .name-cell {
        width: 100%;
      }
    }
  }
}
</style>
